import React, { useEffect, useRef } from "react";
import { Check, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useSepaBankAccounts } from "../../../../stores/Sepa/sepa.queries";
import { maskIbanForDisplay } from "../../../../stores/Sepa/sepa.validation";
import {
  BankAccountWithMandatesDto,
  MandateStatusEnum,
  MandateSequenceTypeEnum,
} from "../../../../stores/Sepa/sepa.types";

/**
 * Resolved bank selection: either reuse a stored account by id, or provide inline
 * details for a new one. The backend `findOrCreate` de-duplicates, so "new" with an
 * IBAN that already exists simply reuses it.
 */
export type BankSelection =
  | { mode: "existing"; bankAccountId: string }
  | { mode: "new"; holderFirstName: string; holderLastName: string; iban: string; bic: string };

export const emptyNewBank: BankSelection = {
  mode: "new",
  holderFirstName: "",
  holderLastName: "",
  iban: "",
  bic: "",
};

const fmtDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString("de-DE") : "";

/** One-line summary of the live (draft/active) mandates already on an account. */
const mandateContext = (account: BankAccountWithMandatesDto): string | null => {
  const live = (account.mandates ?? []).filter(
    (m) => m.status === MandateStatusEnum.Active || m.status === MandateStatusEnum.Draft,
  );
  if (live.length === 0) return null;
  const primary = live[0];
  const type = primary.sequenceType === MandateSequenceTypeEnum.Rcur ? "Mehrfach" : "Einmal";
  const signed = primary.signedDate ? `signiert ${fmtDate(primary.signedDate)}` : "nicht signiert";
  const parts = [primary.accountName, type, signed].filter(Boolean);
  const extra = live.length > 1 ? ` · +${live.length - 1} weitere` : "";
  return `${parts.join(" · ")}${extra}`;
};

/** Prefer an account that already carries a live mandate, else the first one. */
const bestDefaultAccountId = (accounts: BankAccountWithMandatesDto[]): string | undefined => {
  const withLive = accounts.find((a) =>
    (a.mandates ?? []).some(
      (m) => m.status === MandateStatusEnum.Active || m.status === MandateStatusEnum.Draft,
    ),
  );
  return (withLive ?? accounts[0])?.id;
};

interface Props {
  patientId: number;
  value: BankSelection;
  onChange: (value: BankSelection) => void;
  /** Field-level errors keyed by field name (from `validateBankDetails`). */
  errors?: Record<string, string>;
  disabled?: boolean;
}

/**
 * Reusable bank-account picker for the SEPA dialogs (mandate / einmal-Lastschrift /
 * refund). Stored accounts are shown as one-click cards (no dropdown) and a final
 * "Neue Bankverbindung" card reveals inline holder + IBAN + BIC fields.
 */
const BankAccountSelector: React.FC<Props> = ({ patientId, value, onChange, errors, disabled }) => {
  const { data: bankAccounts = [], isLoading } = useSepaBankAccounts(patientId);
  const hasAccounts = bankAccounts.length > 0;

  const patchNew = (patch: Partial<Extract<BankSelection, { mode: "new" }>>) => {
    if (value.mode !== "new") return;
    onChange({ ...value, ...patch });
  };

  // Preselect an existing account so the user lands on a reusable bank instead of
  // the empty "new" form. Done once per patient; the user can still pick "Neue".
  const didDefault = useRef(false);
  useEffect(() => {
    didDefault.current = false;
  }, [patientId]);
  useEffect(() => {
    if (didDefault.current || isLoading || !hasAccounts) return;
    const untouchedNew =
      value.mode === "new" &&
      !value.iban &&
      !value.holderFirstName &&
      !value.holderLastName &&
      !value.bic;
    if (!untouchedNew) return;
    const best = bestDefaultAccountId(bankAccounts);
    if (best) {
      onChange({ mode: "existing", bankAccountId: best });
      didDefault.current = true;
    }
  }, [isLoading, hasAccounts, bankAccounts, value, onChange]);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-base">Bankverbindung</Label>

        {isLoading ? (
          <div className="flex h-11 items-center rounded-lg border border-slate-200 px-3 text-base text-muted-foreground">
            Lädt…
          </div>
        ) : (
          hasAccounts && (
            <div role="radiogroup" aria-label="Bankverbindung" className="space-y-2">
              {bankAccounts.map((account) => {
                const selected = value.mode === "existing" && value.bankAccountId === account.id;
                const context = mandateContext(account);
                return (
                  <button
                    key={account.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    onClick={() => onChange({ mode: "existing", bankAccountId: account.id })}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      selected
                        ? "border-[#4189a8] bg-sky-50 ring-1 ring-[#4189a8]"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="text-base text-slate-800">
                        {account.holderFirstName} {account.holderLastName} ·{" "}
                        <span className="font-mono">{maskIbanForDisplay(account.iban)}</span>
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {context ?? "Kein aktives Mandat"}
                      </span>
                    </span>
                    {selected && <Check className="h-5 w-5 shrink-0 text-[#367b9a]" />}
                  </button>
                );
              })}

              <button
                type="button"
                role="radio"
                aria-checked={value.mode === "new"}
                disabled={disabled}
                onClick={() => onChange(emptyNewBank)}
                className={`flex w-full items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-left text-base transition-colors disabled:opacity-60 ${
                  value.mode === "new"
                    ? "border-[#4189a8] bg-sky-50 text-[#367b9a] ring-1 ring-[#4189a8]"
                    : "border-slate-300 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Plus className="h-4 w-4" />
                Neue Bankverbindung
              </button>
            </div>
          )
        )}
      </div>

      {value.mode === "new" && (
        <div
          className={
            hasAccounts
              ? "grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3"
              : "grid grid-cols-2 gap-3"
          }
        >
          <div className="space-y-1.5">
            <Label htmlFor="sepa-holder-first" className="text-base">
              Vorname
            </Label>
            <Input
              id="sepa-holder-first"
              className="h-11 text-base"
              value={value.holderFirstName}
              onChange={(e) => patchNew({ holderFirstName: e.target.value })}
              disabled={disabled}
            />
            {errors?.holderFirstName && (
              <p className="text-sm text-destructive">{errors.holderFirstName}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sepa-holder-last" className="text-base">
              Nachname
            </Label>
            <Input
              id="sepa-holder-last"
              className="h-11 text-base"
              value={value.holderLastName}
              onChange={(e) => patchNew({ holderLastName: e.target.value })}
              disabled={disabled}
            />
            {errors?.holderLastName && (
              <p className="text-sm text-destructive">{errors.holderLastName}</p>
            )}
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="sepa-iban" className="text-base">
              IBAN
            </Label>
            <Input
              id="sepa-iban"
              className="h-11 text-base"
              value={value.iban}
              onChange={(e) => patchNew({ iban: e.target.value.toUpperCase() })}
              placeholder="DE00 0000 0000 0000 0000 00"
              disabled={disabled}
            />
            {errors?.iban && <p className="text-sm text-destructive">{errors.iban}</p>}
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="sepa-bic" className="text-base">
              BIC (optional)
            </Label>
            <Input
              id="sepa-bic"
              className="h-11 text-base"
              value={value.bic}
              onChange={(e) => patchNew({ bic: e.target.value.toUpperCase() })}
              disabled={disabled}
            />
            {errors?.bic && <p className="text-sm text-destructive">{errors.bic}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountSelector;
